import type { Components, PaletteColor, Theme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";

import type { ColorName } from "../../options/palette";

const SLIDER_RAIL_WIDTH = "--MuiSlider-railWidth";

export const MuiSlider: Components<Theme>["MuiSlider"] = {
  defaultProps: {
    color: "primary",
  },

  styleOverrides: {
    root: ({ theme }) => ({
      [SLIDER_RAIL_WIDTH]: theme.spacing(0.25),

      display: "block",
      height: theme.spacing(3),
      margin: 0,
      padding: 0,
    }),

    mark: {
      display: "none",
    },

    markLabel: ({ theme }) => ({
      // render above slider
      bottom: "100%",
      fontWeight: theme.typography.fontWeightMedium,
      top: "unset",
      userSelect: "none",
    }),

    rail: ({ theme }) => ({
      backgroundColor: "currentColor",
      borderRadius: 0,
      height: "100%",
      opacity: 1,

      // draw rail as narrow horizontal bar with bookend ticks
      // draw polygons clockwise to avoid negation on overlap
      clipPath: `shape(
        from 0 0,
        /* horizontal bar */
        move to 0 calc(50% - var(${SLIDER_RAIL_WIDTH}) / 2), hline to 100%, vline by var(${SLIDER_RAIL_WIDTH}), hline to 0, close,
        /* left bookend */
        move to var(${SLIDER_RAIL_WIDTH}) 25%, vline to 75%, hline to 0, vline to 25%, close,
        /* right bookend */
        move to calc(100% - var(${SLIDER_RAIL_WIDTH})) 75%, vline to 25%, hline to 100%, vline to 75%, close
      )`,
      // extend 1/2 rail width in each direction to center the bookends
      left: `calc(-0.5 * var(${SLIDER_RAIL_WIDTH}))`,
      width: `calc(100% + var(${SLIDER_RAIL_WIDTH}))`,

      variants: [
        ...Object.entries(theme.palette)
          .filter((args): args is [ColorName, PaletteColor] => {
            const [, values] = args;
            return typeof values === "object" && "light" in values;
          })
          .map(([colorName, colorValues]) => ({
            props: {
              color: colorName,
            },
            style: {
              color: colorValues.main,
            },
          })),
        {
          props: {
            disabled: true,
          },
          style: {
            color: theme.palette.grey[700],
          },
        },
      ],
    }),

    thumb: ({ theme }) => ({
      aspectRatio: "1 / 2",
      borderRadius: 0,
      height: "100%",
      width: "unset",
      // DX Grid table header row has a zIndex of 301/302
      // "!important" is to override MUI@9's inline value
      zIndex: "303 !important",

      variants: [
        ...Object.entries(theme.palette)
          .filter((args): args is [ColorName, PaletteColor] => {
            const [, values] = args;
            return typeof values === "object" && "main" in values;
          })
          .map(([colorName, colorValues]) => ({
            props: {
              color: colorName,
            },
            style: {
              backgroundColor: colorValues.dark,
              "&:hover, &.Mui-focusVisible, &.Mui-active": {
                boxShadow: `0 0 0 4px ${alpha(colorValues.main, 0.2)}`,
              },
            },
          })),
        {
          props: {
            disabled: true,
          },
          style: {
            backgroundColor: theme.palette.grey[700],
          },
        },
      ],
    }),

    track: {
      border: "none",
      borderRadius: 0,
      height: "100%",
      opacity: 0.3,
    },

    valueLabel: ({ theme }) => ({
      userSelect: "none",

      variants: Object.entries(theme.palette)
        .filter((args): args is [ColorName, PaletteColor] => {
          const [, values] = args;
          return (
            typeof values === "object" &&
            "main" in values &&
            "contrastText" in values
          );
        })
        .map(([color, values]) => ({
          props: {
            color,
          },
          style: {
            color: values.contrastText,
            backgroundColor: values.main,
            "& > span": {
              backgroundColor: values.main,
            },
          },
        })),
    }),
  },
};
